import type { ClientTool } from "./ClientTools";

/**
 * Approval modes that control when a client-side tool requires user
 * confirmation before executing.
 */
export enum ApprovalMode {
  /** Always show a confirmation modal before executing the tool. */
  REQUIRE_APPROVAL = "require_approval",
  /** Show a confirmation modal when the Auto toggle is OFF; auto-execute when ON. */
  FOLLOW_AUTO = "follow_auto",
  /** Always execute immediately without showing a confirmation modal. */
  AUTO_EXECUTE = "auto_execute",
}

/**
 * What the app knows of the person's saved tool permissions.
 *
 * - `loaded`: the saved record arrived. `preferences` is `null` when nothing
 *   is saved.
 * - `loading`: a request for the record is in flight.
 * - `unavailable`: there is no record and no request for it in flight, for
 *   example because the last request failed.
 */
export type SavedToolPermissions =
  | { status: "loaded"; preferences: Record<string, ApprovalMode> | null }
  | { status: "loading" }
  | { status: "unavailable" };

/**
 * Resolve the approval mode the person's saved preferences give a tool,
 * taking into account the tool's allowable modes.
 *
 * Falls back to the tool's default if:
 * - No user preferences exist
 * - The stored preference is not in the tool's allowable modes
 *   (e.g., after an update restricted options)
 */
export function resolveApprovalMode(
  tool: ClientTool,
  userPreferences: Record<string, ApprovalMode> | null,
): ApprovalMode {
  const stored = userPreferences?.[tool.spec.name];

  if (stored && tool.allowableApprovalModes.includes(stored)) {
    return stored;
  }

  return tool.defaultApprovalMode;
}

/**
 * The approval mode a call to `tool` is decided under.
 *
 * Saved permissions that are not loaded cannot say that a tool may run
 * unasked, and a tool's default may be looser than what the person saved. The
 * mode is then REQUIRE_APPROVAL, whichever modes the tool allows.
 */
export function effectiveApprovalMode(
  tool: ClientTool,
  savedPermissions: SavedToolPermissions,
): ApprovalMode {
  if (savedPermissions.status !== "loaded") {
    return ApprovalMode.REQUIRE_APPROVAL;
  }
  return resolveApprovalMode(tool, savedPermissions.preferences);
}

/** Whether a call to `tool` waits for the person to approve it. */
export function toolCallNeedsApproval(
  tool: ClientTool,
  savedPermissions: SavedToolPermissions,
  autoEnabled: boolean,
): boolean {
  return needsApproval(
    effectiveApprovalMode(tool, savedPermissions),
    autoEnabled,
  );
}

/**
 * Determine whether a tool invocation needs user approval given the
 * resolved approval mode and the current Auto toggle state.
 */
export function needsApproval(
  mode: ApprovalMode,
  autoEnabled: boolean,
): boolean {
  switch (mode) {
    case ApprovalMode.REQUIRE_APPROVAL:
      return true;
    case ApprovalMode.FOLLOW_AUTO:
      return !autoEnabled;
    case ApprovalMode.AUTO_EXECUTE:
      return false;
  }
}
